import { DayInfo, YearMonth } from 'appTypes';
import dayjs from 'dayjs';

import {
  dateTimeToString,
  isEndAfterStart,
  parseDate,
  parseDateTimeIgnoringTz,
} from '../datetimeUtils';

export const DEFAULT_HOURS = 24;
export const MAX_HOURS = 30;

export const DATE_PICKER_DEFAULT_SIZE = '252px';

export const fillYears = (a: number, b: number) =>
  Array.from({ length: Math.abs(a - b) + 1 }, (_, i) =>
    (Math.min(a, b) + i).toString(),
  );

const getMeridian = (hour: number) => {
  return hour < 12 ? 'AM' : 'PM';
};

export const formatDateTimeToLocalLabel = (d: string): string => {
  const date = parseDateTimeIgnoringTz(d);
  const day = date.date();
  const month = date.locale('en').format('MMM'); // local month
  const hour = date.hour();

  return `${day} ${month} (${getHourPadded(hour)} ${getMeridian(hour)})`;
};
/** 07:15 AM -> 07 AM */
export const getOnlyAmPMHours = (time: string) => {
  if (!time) return undefined;
  return `${time.substring(0, 2)}${time.substring(5, 8)}`;
};

export const formatTimeAmPm = (d: string): string => {
  const date = dayjs(d);
  const hours = date.hour();
  const minutes = date.minute().toString().padStart(2, '0');
  const meridian = getMeridian(hours);
  return `${getHourPadded(hours)}:${minutes} ${meridian}`;
};

/** difference between time1 and time2 must be max of 24hours */
export function diffHours(time1: string, time2: string): number {
  const [h1, m1] = time1.split(':').map(Number);
  const [h2, m2] = time2.split(':').map(Number);

  const t1 = h1 + m1 / 60;
  const t2 = h2 + m2 / 60;

  let diff = (t2 - t1 + 24) % 24;
  if (diff === 0) diff = 24;
  return diff;
}

export const hoursBetween = (isoA: string, isoB: string): number => {
  const msA = dayjs(isoA).valueOf();
  const msB = dayjs(isoB).valueOf();
  const diffMs = Math.abs(msB - msA);
  return diffMs / (1000 * 60 * 60);
};

export function diffInHours(start: dayjs.Dayjs, end: dayjs.Dayjs) {
  return end.diff(start, 'hour');
}

export function diffInCalendarDays(start: dayjs.Dayjs, end: dayjs.Dayjs) {
  return end.diff(start, 'days') + 1;
}

export function areDatesConsecutive(dates: string[]): boolean {
  for (let i = 1; i < dates.length; i++) {
    if (parseDate(dates[i]).diff(parseDate(dates[i - 1]), 'day') !== 1) {
      return false;
    }
  }
  return true;
}

export const isSameDate = (d1: dayjs.Dayjs, d2: dayjs.Dayjs): boolean =>
  d1.isSame(d2, 'day');

export const wholeHoursLeftInDay = (datetime: string): number => {
  const date = parseDateTimeIgnoringTz(datetime);
  const startOfTomorrow = date.add(1, 'day').startOf('day');
  const diffMs = startOfTomorrow.valueOf() - date.valueOf();
  return Math.floor(diffMs / (1000 * 60 * 60));
};

export function calculateMaxHoursForDate(
  date: string,
  datesInfo: DayInfo[],
): number {
  const d = parseDate(date);
  const next = d.add(1, 'day');
  const hasNextDay = datesInfo.some((el) => isSameDate(dayjs(el.date), next));
  return !hasNextDay ? wholeHoursLeftInDay(date) : MAX_HOURS;
}

/** Fix RM range only when URL params are invalid. */
export function getCorrectedRmDateTimeRange(
  startDateTime: string,
  endDateTime: string,
  datesInfo: DayInfo[],
): { start: string; end: string } | null {
  if (!datesInfo.length) return null;

  const startDay = parseDateTimeIgnoringTz(startDateTime).format('YYYY-MM-DD');
  const startInvalid = !datesInfo.some((info) => info.date === startDay);

  const start = startInvalid
    ? makeLocalDateFromStrings(
        datesInfo[datesInfo.length - 1].date,
        formatTimeAmPm(startDateTime) || DEFAULT_TIME_WITH_MERIDIEM,
      )
    : startDateTime;

  const maxHours = calculateMaxHoursForDate(start, datesInfo);
  const spanHours = hoursBetween(endDateTime, startDateTime);
  const durationValid =
    isEndAfterStart(start, endDateTime) &&
    hoursBetween(endDateTime, start) <= maxHours;

  if (!startInvalid && durationValid) return null;

  const end = dateTimeToString(
    parseDateTimeIgnoringTz(start).add(
      Math.min(maxHours, spanHours || DEFAULT_HOURS),
      'hour',
    ),
  );

  return { start, end };
}

const getHourPadded = (i: number): string => {
  const hour = i % 12 === 0 ? 12 : i % 12;
  return hour.toString().padStart(2, '0');
};

export const DEFAULT_TIME_WITH_MERIDIEM = '12:00 AM';
export const DEFAULT_TIME = '00:00';
export const HOURS = Array.from({ length: 24 }, (_, i) => {
  const suffix = i < 12 ? 'AM' : 'PM';
  return `${getHourPadded(i)} ${suffix}`;
});

export const AM_HOURS = Array.from({ length: 12 }, (_, i) => {
  return getHourPadded(i + 1);
});
export const MIN_BINS = ['00', '15', '30', '45'];

export const parse12HourString = (
  time12h: string,
): { hours: number; minutes: number } => {
  const timeArr = time12h.split(/[:\s]+/);

  const hour12 = parseInt(timeArr[0], 10);
  const minutes = parseInt(timeArr[1], 10);
  const meridiem = timeArr[2];

  let hour24: number;
  if (meridiem === 'AM') {
    hour24 = hour12 % 12;
  } else {
    hour24 = (hour12 % 12) + 12;
  }
  // return default values if time is invalid (e.g., incorrect params)
  if (
    Number.isNaN(hour24) ||
    Number.isNaN(minutes) ||
    hour24 < 0 ||
    hour24 > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return { hours: 0, minutes: 0 };
  }
  return { hours: hour24, minutes };
};

export function convertTo12Hour(time24: string): string {
  const [hourStr, minute] = time24.split(':');
  let hour = parseInt(hourStr, 10);

  const meridian = getMeridian(hour);
  hour = hour % 12 || 12;

  return `${getHourPadded(hour)}:${minute} ${meridian}`;
}

export function convertTo24HourString(time12: string): string {
  const [timePart, meridianRaw] = time12.trim().split(/\s+/);
  const [hourStr, minute] = timePart.split(':');
  const meridian = meridianRaw.toUpperCase();

  let hour = parseInt(hourStr, 10);

  if (meridian === 'AM' && hour === 12) hour = 0;
  else if (meridian === 'PM' && hour !== 12) hour += 12;

  return `${hour.toString().padStart(2, '0')}:${minute}`;
}

export function calculateEndHourAndLabel(startHour: string, hours: number) {
  const [hourStr, minuteStr] = convertTo24HourString(startHour).split(':');

  const currHour = parseInt(hourStr, 10);
  const currMin = parseInt(minuteStr, 10);

  const minsToAdd = (hours % 1) * 60;
  const hoursToAdd = Math.floor(hours);

  let totalHours = currHour + hoursToAdd;
  const newMin = currMin + minsToAdd;
  totalHours += Math.floor(newMin / 60);
  const daysRolled = totalHours / 24;
  totalHours %= 24;
  const minsRemainder = newMin % 60;

  // Construct new 24h time string
  const newHourStr = totalHours.toString().padStart(2, '0');
  const newTime24 = `${newHourStr}:${minsRemainder.toString().padStart(2, '0')}`;
  const endHour = convertTo12Hour(newTime24);

  let dayLabel = '';
  if (daysRolled >= 2) dayLabel = 'day after';
  else if (daysRolled >= 1) dayLabel = 'next day';

  return { endHour, dayLabel, daysRolled };
}

export const makeLocalDateFromStrings = (
  dateStr: string,
  time12h: string,
  nextDay: boolean = false,
): string => {
  const { hours, minutes } = parse12HourString(time12h);

  let date = parseDate(dateStr)
    .hour(hours)
    .minute(minutes)
    .second(0)
    .millisecond(0);

  if (!date.isValid()) {
    console.error(
      `Invalid date format ${date} (hours: ${hours}, min: ${minutes}) for ${dateStr}, ${time12h}.`,
    );
  }

  if (nextDay) date = date.add(1, 'day');
  return dateTimeToString(date);
};

/** MonthYearMenu */
export const ALL_MONTHS = [
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
] as const;

export const parseYearMonth = (
  value: string,
): { year: number; month: number } => {
  const [yearStr, monthStr] = value.split('-');
  return {
    year: Number(yearStr),
    month: Number(monthStr),
  };
};
export const buildYearMonth = (
  year: number | string,
  month: number | string,
): YearMonth => `${year}-${String(month).padStart(2, '0')}` as YearMonth;
